/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useState } from "react"
import * as S from "./styled"

import Header from "../../components/Header"
import Footer from "../../components/Footer"
import Container from "../../components/Container"

import getStore from "../../store"
import { useLocation, useNavigate, useNavigationType } from "react-router-dom"
import TicketCard from "../../components/TicketCard"
import { Api } from "../../api"
import { isGuestSession } from "../../utils/tb/session"

import { DotLottieReact } from "@lottiefiles/dotlottie-react"
import loadingAnimation from "../../assets/animations/loading"
import { TEventData } from "../../utils/@types/data/event"
import { DEFAULT_EVENT_END_DATE_YEAR } from "../../utils/consts/defaultEndDate"

import { Calendar, Ticket } from "lucide-react"

type FilterKey = "all" | "active" | "cancelled"

const MyTickets = () => {
  const { controllers, user, event } = getStore()

  const lctn = useLocation()
  const navigate = useNavigate()
  const navigationType = useNavigationType()

  useEffect(() => {
    if (!user) {
      navigate("/", { replace: true })
    }
  }, [user, navigate])

  const [loading, setLoading] = useState(true)
  const [list, setList] = useState<any[]>([])
  const [filter, setFilter] = useState<FilterKey>("all")

  useEffect(() => {
    if (navigationType === "POP") {
      if (lctn.state?.backRoute) {
        navigate(lctn.state.backRoute, { replace: true })
      }
    }
  }, [navigationType])

  const sortList = (list: any[]) => {
    const sortedList = list.sort((a, b) => {
      const aTime = new Date(a.date).getTime()
      const bTime = new Date(b.date).getTime()

      if (!Number.isNaN(aTime) && !Number.isNaN(new Date(b.date).getTime())) {
        return aTime > bTime ? -1 : 1
      } else return 0
    })

    return sortedList
  }

  const getData = useCallback(async (eventInfo: any) => {
    // Comprador sem login não tem conta: nem tenta a API, pede o código.
    // Sem limpar a sessão aqui: com o user nulo, os efeitos desta página
    // mandariam para / ou /eventSelect por cima do /login. O Login limpa
    // user/token/logged ao montar; os tokens de pedido ficam (só o logout).
    if (isGuestSession(getStore.getState().user)) {
      navigate("/login", { replace: true })
      return
    }

    setLoading(true)

    try {
      const req = await Api.get.myTickets({
        eventId: eventInfo?.id as string,
        eventName: eventInfo?.name as string,
      })

      if (req.ok) {
        const available = req.data.list.filter((i) => {
          let status = true

          if (i.payments.length > 0) {
            const payment = i.payments[0]
            const isPayed =
              payment.transition_code ||
              payment.machine_data ||
              i.status === "validado" ||
              i.status === "cancelamento"

            status = isPayed
          } else status = false

          return status
        })

        let updatedEventInfo: TEventData | null = null

        if (eventInfo) {
          const req2 = await Api.get.eventInfo({ eventId: eventInfo?.id })

          if (req2.ok) {
            const data = req2.data
            if (Boolean(data.is_ecommerce)) {
              controllers.event.setData(req2.data)
              updatedEventInfo = req2.data
              sessionStorage.setItem("event", JSON.stringify(req2.data))
            } else {
              controllers.event.clear()
              navigate("/eventSelect")
            }
          }
        }

        const newList = available.map((i) => ({
          ...i,
          eventBanner: (updatedEventInfo ?? eventInfo).event_banner as string,
          products: i.products.map((ip: any) => ({
            ...ip,
            TRN: i.extref,
            payment_type: ip.payment_type ?? i.payments[0]?.payment_type,
          })),
        }))

        let activeOrders: any[] = []
        let otherOrders: any[] = []

        newList.forEach((i) => {
          if (i.status === "validado") activeOrders.push(i)
          else otherOrders.push(i)
        })

        const sortedList = [...sortList(activeOrders), ...sortList(otherOrders)]

        setList(sortedList)
      } else if (req.code === "LOGIN_REQUIRED") {
        // Compra sem login: o token só abre o pedido; a lista pede o código
        // (o Login limpa a sessão ao montar, ver acima).
        navigate("/login", { replace: true })
        return
      }
    } catch (error) {}

    setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!!user) {
      const eventData = sessionStorage.getItem("event")

      if (eventData) {
        getData(JSON.parse(eventData))
      } else if (event) {
        // sessionStorage pode ter sido limpo (ex.: passagem por /eventSelect) mesmo
        // com o evento ainda presente no store — evita mandar o usuário logado de
        // volta pra seleção de eventos quando o contexto já existe em memória.
        sessionStorage.setItem("event", JSON.stringify(event))
        getData(event)
      } else {
        navigate("/eventSelect")
      }
    } else {
      navigate("/eventSelect")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controllers.event, getData, navigate, user])

  const counts = useMemo(() => {
    const active = list.filter((i) => i.status === "validado").length
    const cancelled = list.filter((i) => i.status === "cancelamento").length
    return { all: list.length, active, cancelled }
  }, [list])

  const filteredList = useMemo(() => {
    if (filter === "active") return list.filter((i) => i.status === "validado")
    if (filter === "cancelled")
      return list.filter((i) => i.status === "cancelamento")
    return list
  }, [list, filter])

  const renderEventDate = () => {
    if (event) {
      const iniDate = new Date(event.date_ini)
      const endDate = new Date(event.date_end)

      const finalIniDate = iniDate.getUTCDate()
      const finalIniMonth = String(iniDate.getUTCMonth() + 1).padStart(2, "0")
      const finalIniYear = iniDate.getUTCFullYear()

      const finalEndDate = endDate.getUTCDate()
      const finalEndMonth = String(endDate.getUTCMonth() + 1).padStart(2, "0")
      const finalEndYear = endDate.getUTCFullYear()

      const formattedIniDate = `${finalIniDate}/${finalIniMonth}/${finalIniYear}`
      const formattedEndDate = `${finalEndDate}/${finalEndMonth}/${finalEndYear}`

      const eventDateText =
        event.date_end === event.date_ini ||
        endDate.getTime() < iniDate.getTime() ||
        endDate.getFullYear() >= DEFAULT_EVENT_END_DATE_YEAR
          ? formattedIniDate
          : `${formattedIniDate} até ${formattedEndDate}`

      return eventDateText
    }
  }

  const segments: { key: FilterKey; label: string; count: number }[] = [
    { key: "all", label: "Todas", count: counts.all },
    { key: "active", label: "Ativas", count: counts.active },
    { key: "cancelled", label: "Canceladas", count: counts.cancelled },
  ]

  const renderEmptyState = (scope: FilterKey) => {
    const copy = {
      all: {
        title: "Nenhum ingresso por aqui ainda",
        text: "Quando você comprar, seus ingressos aparecem aqui.",
      },
      active: {
        title: "Nenhuma compra ativa",
        text: "Você ainda não tem compras ativas para este evento.",
      },
      cancelled: {
        title: "Nenhuma compra cancelada",
        text: "Todas as suas compras estão ativas.",
      },
    }[scope]

    return (
      <S.EmptyState role="status" aria-live="polite">
        <S.EmptyIllustration aria-hidden="true">
          <Ticket />
        </S.EmptyIllustration>
        <S.EmptyTitle>{copy.title}</S.EmptyTitle>
        <S.EmptyText>{copy.text}</S.EmptyText>
        {scope === "all" && (
          <S.EmptyCta type="button" onClick={() => navigate("/eventSelect")}>
            Ver eventos
          </S.EmptyCta>
        )}
      </S.EmptyState>
    )
  }

  return (
    <S.Page>
      <Header />

      <Container fullHeight={true}>
        <S.Main>
          <S.HeaderArea>
            <S.PageTitle>Meus ingressos</S.PageTitle>
            <S.EventDateChip>
              <Calendar aria-hidden="true" />
              {renderEventDate()}
            </S.EventDateChip>
          </S.HeaderArea>

          {!loading && list.length > 0 && (
            <S.Segmented role="tablist" aria-label="Filtrar compras">
              {segments.map((seg) => {
                const active = filter === seg.key
                return (
                  <S.SegmentedButton
                    key={seg.key}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    $active={active}
                    onClick={() => setFilter(seg.key)}
                  >
                    {seg.label}
                    <S.SegmentedCount $active={active}>
                      {seg.count}
                    </S.SegmentedCount>
                  </S.SegmentedButton>
                )
              })}
            </S.Segmented>
          )}

          {loading ? (
            <S.LoadingWrapper>
              <DotLottieReact
                data={loadingAnimation}
                loop
                autoplay
                width={"100%"}
              />
            </S.LoadingWrapper>
          ) : list.length === 0 ? (
            renderEmptyState("all")
          ) : filteredList.length === 0 ? (
            renderEmptyState(filter)
          ) : (
            <S.List>
              {filteredList.map((ticket, k) => (
                <TicketCard k={k} key={ticket.id ?? ticket.extref ?? k} data={ticket} />
              ))}
            </S.List>
          )}
        </S.Main>
      </Container>

      <Footer />
    </S.Page>
  )
}

export default MyTickets
